<template>
  <!-- Floats at the active table's top-right corner — anchored to the TABLE
       element (not the caret), so it stays put while typing, never covers
       rows, and never drifts into the corner where the autosave badge lives.
       A separate bubble-menu plugin instance (own pluginKey) so it never
       fights the formatting bubble (which needs a non-empty selection). -->
  <BubbleMenu
    :editor="editor"
    plugin-key="rteTableMenu"
    :should-show="shouldShow"
    :get-referenced-virtual-element="tableVirtualElement"
    :update-delay="100"
    :options="{ placement: 'top-end', offset: 6, shift: { padding: 8 } }"
  >
    <div class="table-menu">
      <button
        type="button"
        class="table-menu-btn rte-tt"
        :aria-label="t('messages.editor.tableRowAdd')"
        :data-tt="t('messages.editor.tableRowAdd')"
        @click="editor.chain().focus().addRowAfter().run()"
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
          <rect x="2" y="2.5" width="12" height="4.5" rx="1" />
          <path d="M8 9.5v4.5M5.75 11.75h4.5" />
        </svg>
      </button>
      <button
        type="button"
        class="table-menu-btn rte-tt"
        :aria-label="t('messages.editor.tableRowRemove')"
        :data-tt="t('messages.editor.tableRowRemove')"
        @click="editor.chain().focus().deleteRow().run()"
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
          <rect x="2" y="2.5" width="12" height="4.5" rx="1" />
          <path d="M5.75 11.75h4.5" />
        </svg>
      </button>

      <span class="table-menu-divider"></span>

      <button
        type="button"
        class="table-menu-btn rte-tt"
        :aria-label="t('messages.editor.tableColAdd')"
        :data-tt="t('messages.editor.tableColAdd')"
        @click="editor.chain().focus().addColumnAfter().run()"
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
          <rect x="2.5" y="2" width="4.5" height="12" rx="1" />
          <path d="M9.5 8H14M11.75 5.75v4.5" />
        </svg>
      </button>
      <button
        type="button"
        class="table-menu-btn rte-tt"
        :aria-label="t('messages.editor.tableColRemove')"
        :data-tt="t('messages.editor.tableColRemove')"
        @click="editor.chain().focus().deleteColumn().run()"
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round">
          <rect x="2.5" y="2" width="4.5" height="12" rx="1" />
          <path d="M9.5 8H14" />
        </svg>
      </button>

      <span class="table-menu-divider"></span>

      <button
        type="button"
        class="table-menu-btn rte-tt"
        :aria-label="t('messages.editor.tableDelete')"
        :data-tt="t('messages.editor.tableDelete')"
        @click="editor.chain().focus().deleteTable().run()"
      >
        <span class="pi pi-trash text-xs"></span>
      </button>
    </div>
  </BubbleMenu>
</template>

<script setup>
import { BubbleMenu } from '@tiptap/vue-3/menus'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  editor: { type: Object, required: true },
})

const { t } = useI18n()

const shouldShow = ({ editor: instance, state }) =>
  instance.isEditable && state.selection.empty && instance.isActive('table')

// Anchor: the table's DOM node, resolved from the caret's ancestor chain.
const tableVirtualElement = () => {
  const { editor } = props
  const { $from } = editor.state.selection
  for (let depth = $from.depth; depth > 0; depth--) {
    if ($from.node(depth).type.name === 'table') {
      const dom = editor.view.nodeDOM($from.before(depth))
      if (dom instanceof HTMLElement) {
        return {
          getBoundingClientRect: () => dom.getBoundingClientRect(),
          getClientRects: () => [dom.getBoundingClientRect()],
        }
      }
    }
  }
  return undefined
}
</script>

<style scoped>
  @reference '../../../css/tailwind.css';

  /* Light pill — deliberately the inverse of the dark formatting bubble, so
     the two floating menus read as different tools at a glance. */
  .table-menu {
    @apply flex items-center gap-0.5 rounded-full border border-surface-200 bg-white px-1.5 py-1 shadow-lg dark:border-surface-600 dark:bg-surface-800;
    animation: table-menu-pop 0.12s ease-out;
  }

  .table-menu-btn {
    @apply flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-surface-500 transition-colors dark:text-surface-400;

    &:hover {
      @apply bg-surface-100 text-surface-900 dark:bg-surface-700 dark:text-surface-100;
    }
  }

  .table-menu-divider {
    @apply mx-0.5 h-3.5 w-px bg-surface-200 dark:bg-surface-600;
  }

  @keyframes table-menu-pop {
    from {
      opacity: 0;
      transform: translateY(-3px) scale(0.97);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
</style>
